import React, { useState, useEffect, useRef } from 'react'
import { ConnectWebSocketForOverlay, SetSocketMessageHandler, ClearSocketMessageHandler, DisconnectWebSocket } from './../../utilities/BL/Zones'
import { GetKissConfig } from './../../utilities/BL/Kiss'
import { Kiss as KissOverlay } from './Overlays/Kiss'

/**
 * COMPONENTE: CONTENEDOR DE RUTA DE KISS CAM (BESO)
 * ------------------------------------------------------------------
 * 1. Establece la conexión SignalR para "overlay:kiss".
 * 2. Carga la configuración inicial desde la API (JSON en disco).
 * 3. Maneja estados en vivo (tensión, corazón, éxito, abucheo).
 * 4. Gestiona las partículas y popups interactivos.
 * ------------------------------------------------------------------
 */
const Kiss = () => {
    const [config, setConfig] = useState({
        visible: true,
        titleText: 'KISS CAM',
        failText: '¡BUUUUU!',
        frameStyle: 'classic-red',
        frameColor: '#ff0033',
        borderWidth: 6,
        frameZoom: 92,
        lowerThirdBgColor: '#000000',
        lowerThirdBgOpacity: 0.6,
        screenFilter: 'none',
        floatingShape: 'hearts',
        shapeOpacity: 0.3,
        shapeGlow: 15,
        logoDataUrl: null,
        logoCorner: 'top-left',
        logoScale: 1.0,
        scale: 1.0,
        titleSize: 3.5,
        offsetX: 0,
        offsetY: 0
    });

    const [connectionStatus, setConnectionStatus] = useState('idle');

    // Estados de interacción en vivo
    const [tensionActive, setTensionActive] = useState(false);
    const [heartTensionActive, setHeartTensionActive] = useState(false);
    const [successActive, setSuccessActive] = useState(false);
    const [failActive, setFailActive] = useState(false);

    // Listas de elementos animados dinámicamente
    const [particles, setParticles] = useState([]);
    const [besoPopups, setBesoPopups] = useState([]);

    const besoIntervalRef = useRef(null);
    const popupResetTimeoutRef = useRef(null);

    // Configuración inicial de pantalla completa OBS
    useEffect(() => {
        const originalBodyBg = document.body.style.backgroundColor;
        const originalHtmlBg = document.documentElement.style.backgroundColor;
        const originalBodyOverflow = document.body.style.overflow;
        const originalHtmlOverflow = document.documentElement.style.overflow;
        const rootElement = document.getElementById('root');

        document.body.style.backgroundColor = 'transparent';
        document.documentElement.style.backgroundColor = 'transparent';
        document.body.style.overflow = 'hidden';
        document.documentElement.style.overflow = 'hidden';

        if (rootElement) {
            rootElement.style.backgroundColor = 'transparent';
            rootElement.style.overflow = 'hidden';
        }

        return () => {
            document.body.style.backgroundColor = originalBodyBg;
            document.documentElement.style.backgroundColor = originalHtmlBg;
            document.body.style.overflow = originalBodyOverflow;
            document.documentElement.style.overflow = originalHtmlOverflow;
            if (rootElement) {
                rootElement.style.removeProperty('background-color');
                rootElement.style.removeProperty('overflow');
            }
        };
    }, []);

    // Carga de configuración y SignalR
    useEffect(() => {
        const initKiss = async () => {
            const res = await GetKissConfig();
            if (res.success && res.data) {
                setConfig(prev => ({ ...prev, ...res.data }));
            }

            await ConnectWebSocketForOverlay(
                'kiss',
                (status) => setConnectionStatus(status),
                (msg) => console.log('[Kiss Overlay] WS status:', msg)
            );

            SetSocketMessageHandler(handleIncomingMessage);
        };

        const handleIncomingMessage = (message) => {
            if (!message || !message.type) return;

            const msgType = message.type.toLowerCase();
            const payload = message.data;

            if (msgType === 'kiss') {
                if (payload.action === 'config') {
                    setConfig(prev => ({ ...prev, ...payload.config }));
                } else if (payload.action === 'show') {
                    setConfig(prev => ({ ...prev, visible: true }));
                } else if (payload.action === 'hide') {
                    setConfig(prev => ({ ...prev, visible: false }));
                } else if (payload.action === 'success') {
                    triggerSuccess();
                } else if (payload.action === 'fail') {
                    triggerFail();
                } else if (payload.action === 'tension') {
                    setTensionActive(payload.state);
                    if (payload.state) setHeartTensionActive(false);
                } else if (payload.action === 'hearttension') {
                    setHeartTensionActive(payload.state);
                    if (payload.state) setTensionActive(false);
                } else if (payload.action === 'reset') {
                    resetOverlay();
                }
            }
        };

        initKiss();

        return () => {
            ClearSocketMessageHandler();
            DisconnectWebSocket();
            stopBesoPopups();
        };
    }, []);

    // Bucle para spam de beso popups flotantes en Modo 3
    useEffect(() => {
        if (heartTensionActive) {
            startBesoPopups();
        } else {
            stopBesoPopups();
        }
        return () => stopBesoPopups();
    }, [heartTensionActive]);

    const startBesoPopups = () => {
        stopBesoPopups();
        let idCounter = 0;
        besoIntervalRef.current = setInterval(() => {
            const screenW = window.innerWidth;
            const screenH = window.innerHeight;
            const isLeft = Math.random() > 0.5;

            const minX = isLeft ? (screenW * 0.05) : (screenW * 0.70);
            const randX = minX + Math.random() * (screenW * 0.20);
            const randY = screenH * 0.20 + Math.random() * (screenH * 0.55);
            const rot = (Math.random() - 0.5) * 40;

            const newBeso = {
                id: idCounter++,
                text: '¡BESO!',
                x: randX,
                y: randY,
                rot
            };

            setBesoPopups(prev => [...prev, newBeso]);
            setTimeout(() => {
                setBesoPopups(prev => prev.filter(b => b.id !== newBeso.id));
            }, 1800);
        }, 850);
    };

    const stopBesoPopups = () => {
        if (besoIntervalRef.current) {
            clearInterval(besoIntervalRef.current);
            besoIntervalRef.current = null;
        }
        setBesoPopups([]);
    };

    // Detonar explosión de corazones (Beso exitoso)
    const triggerSuccess = () => {
        setSuccessActive(true);
        setFailActive(false);
        setTensionActive(false);
        setHeartTensionActive(false);

        const screenW = window.innerWidth;
        const screenH = window.innerHeight;
        const tempParticles = [];

        // Detonar 40 corazones volando desde abajo al centro
        for (let i = 0; i < 40; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = Math.random() * 250 + 150;
            const endSpeed = Math.random() * 400 + 300;
            
            tempParticles.push({
                id: i,
                x: screenW / 2,
                y: screenH - 120,
                tx: Math.cos(angle) * speed,
                ty: Math.sin(angle) * speed - 150,
                txEnd: Math.cos(angle) * endSpeed,
                tyEnd: Math.sin(angle) * endSpeed + 100,
                char: i % 2 === 0 ? '❤️' : '💋',
                color: '#ff0033',
                delay: Math.random() * 0.15
            });
        }
        setParticles(tempParticles);

        if (popupResetTimeoutRef.current) clearTimeout(popupResetTimeoutRef.current);
        popupResetTimeoutRef.current = setTimeout(() => {
            setSuccessActive(false);
            setParticles([]);
        }, 4000);
    };

    // Detonar abucheo (Beso fallido)
    const triggerFail = () => {
        setFailActive(true);
        setSuccessActive(false);
        setTensionActive(false);
        setHeartTensionActive(false);

        const screenW = window.innerWidth;
        const screenH = window.innerHeight;
        const tempParticles = [];

        // Detonar 30 thumbs down en explosión
        for (let i = 0; i < 30; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = Math.random() * 200 + 100;
            
            tempParticles.push({
                id: i,
                x: screenW / 2,
                y: screenH - 120,
                tx: Math.cos(angle) * speed,
                ty: Math.sin(angle) * speed + 50,
                txEnd: Math.cos(angle) * speed * 1.5,
                tyEnd: Math.sin(angle) * speed * 1.5 + 200,
                char: '👎',
                color: '#ef4444',
                delay: Math.random() * 0.1
            });
        }
        setParticles(tempParticles);

        if (popupResetTimeoutRef.current) clearTimeout(popupResetTimeoutRef.current);
        popupResetTimeoutRef.current = setTimeout(() => {
            setFailActive(false);
            setParticles([]);
        }, 5000);
    };

    const resetOverlay = () => {
        setTensionActive(false);
        setHeartTensionActive(false);
        setSuccessActive(false);
        setFailActive(false);
        setParticles([]);
        stopBesoPopups();
        if (popupResetTimeoutRef.current) clearTimeout(popupResetTimeoutRef.current);
    };

    // Atajos de teclado en OBS Interact
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === '1') {
                triggerSuccess();
            } else if (e.key === '2') {
                triggerFail();
            } else if (e.key === '3') {
                setHeartTensionActive(prev => !prev);
                setTensionActive(false);
            } else if (e.key === '4') {
                setTensionActive(prev => !prev);
                setHeartTensionActive(false);
            } else if (e.key === '0') {
                resetOverlay();
            } else if (e.code === 'Space') {
                e.preventDefault();
                setConfig(prev => ({ ...prev, visible: !prev.visible }));
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    return (
        <KissOverlay 
            config={config}
            tensionActive={tensionActive}
            heartTensionActive={heartTensionActive}
            successActive={successActive}
            failActive={failActive}
            particleList={particles}
            besoPopupList={besoPopups}
        />
    );
};

export { Kiss };
export default Kiss;
